import React, { useState, useEffect } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  BrowserRouter,
  redirect,
} from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import Dashboard from "layouts/dashboard";

import Signin from "Auth/Signin";

import ProtectedRoute from "./ProtectedRoute";

import AddMediaFolder from "AddViews/AddMediaFolder";
import UpdateMediaFolder from "UpdateViews/UpdateMedia";
import AddLevel from "AddViews/AddLevels";
import UpdateLevel from "UpdateViews/UpdateLevel";
import AddCourse from "AddViews/AddCourses";
import UpdateCourses from "UpdateViews/UpdateCourses";
import AddTeam from "AddViews/AddTeams";
import UpdateTeam from "UpdateViews/UpdateTeams";
import AddSubjects from "AddViews/AddSubjects";
import UpdateSubject from "UpdateViews/UpdateSubject";
import AddBatch from "AddViews/AddBatch";
import UpdateBatch from "UpdateViews/UpdateBatch";
import AddAchiever from "AddViews/AddAchiever";
import UpdateAchiever from "UpdateViews/UpdateAchiever";
import AddResults from "AddViews/AddResults";
import UpdateResult from "UpdateViews/UpdateResults";
import AddAnnouncement from "AddViews/AddAnnouncements";
import UpdateAnnouncement from "UpdateViews/UpdateAnnouncements";
import AddPopup from "AddViews/AddPopup";
import UpdatePopup from "UpdateViews/UpdatePopup";
import AddHomeScreen from "AddViews/AddHomeScreen";
import UpdateHomeScreen from "UpdateViews/UpdateHomeScreen";
import AddFaq from "AddViews/AddFaq";
import UpdateFaq from "UpdateViews/UpdateFaq";
import AddGallery from "AddViews/AddGallery";
import UpdateGallery from "UpdateViews/UpdateGallery";
import AddBlogs from "AddViews/AddBlogs";
import UpdateBlog from "UpdateViews/UpdateBlogs";

export default function Main() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);

  // const admin = JSON.parse(localStorage.getItem("admin_details"));

  // Open sidenav when mouse enter on mini
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      // console.log(admin);

      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );
  return (
    <div>
      <Sidenav
        color={sidenavColor}
        brand={
          (transparentSidenav && !darkMode) || whiteSidenav
            ? brandDark
            : brandWhite
        }
        brandName="VPA"
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      />
      <Configurator />
      {configsButton}
      <Routes>
        {/* <Route path="/" element={<Dashboard />} /> */}
        {getRoutes(routes)}

        <Route
          path="/addMediaFolder"
          element={
            <ProtectedRoute
              element={<AddMediaFolder />}
              allowedRoles={["superadmin"]}
            />
          }
        />
        <Route
          path="/updateMediaFolder/:id"
          element={
            <ProtectedRoute
              element={<UpdateMediaFolder />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        {/* Levels */}
        <Route
          path="/addlevels"
          element={
            <ProtectedRoute
              element={<AddLevel />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updatelevel/:id"
          element={
            <ProtectedRoute
              element={<UpdateLevel />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        {/* Courses */}
        <Route
          path="/addcourses"
          element={
            <ProtectedRoute
              element={<AddCourse />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updatecourses/:id"
          element={
            <ProtectedRoute
              element={<UpdateCourses />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addteams"
          element={
            <ProtectedRoute
              element={<AddTeam />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updateteams/:id"
          element={
            <ProtectedRoute
              element={<UpdateTeam />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addsubjects"
          element={
            <ProtectedRoute
              element={<AddSubjects />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updatesubject/:id"
          element={
            <ProtectedRoute
              element={<UpdateSubject />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addbatches"
          element={
            <ProtectedRoute
              element={<AddBatch />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updatebatch/:id"
          element={
            <ProtectedRoute
              element={<UpdateBatch />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addachiever"
          element={
            <ProtectedRoute
              element={<AddAchiever />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/updateachiever/:id"
          element={
            <ProtectedRoute
              element={<UpdateAchiever />}
              allowedRoles={["superadmin"]}
            />
          }
        />

 <Route
          path="/addresults"
          element={
            <ProtectedRoute
              element={<AddResults />}
              allowedRoles={["superadmin"]}
            />
          }
        />

         <Route
          path="/updateResult/:id"
          element={
            <ProtectedRoute
              element={<UpdateResult />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addannouncement"
          element={
            <ProtectedRoute
              element={<AddAnnouncement />}
              allowedRoles={["superadmin"]}
            />
          }
        />

 
        <Route
          path="/updateannouncement/:id"
          element={
            <ProtectedRoute
              element={<UpdateAnnouncement />}
              allowedRoles={["superadmin"]}
            />
          }
        />
 <Route
          path="/addpopup"
          element={
            <ProtectedRoute
              element={<AddPopup />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/updatepopup/:id"
          element={
            <ProtectedRoute
              element={<UpdatePopup />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addhome"
          element={
            <ProtectedRoute
              element={<AddHomeScreen />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/updatehome/:id"
          element={
            <ProtectedRoute
              element={<UpdateHomeScreen />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/addfaq"
          element={
            <ProtectedRoute
              element={<AddFaq />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/updatefaq/:id"
          element={
            <ProtectedRoute
              element={<UpdateFaq />}
              allowedRoles={["superadmin"]}
            />
          }
        />

        <Route
          path="/addgallery"
          element={
            <ProtectedRoute
              element={<AddGallery />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/updategallery/:id"
          element={
            <ProtectedRoute
              element={<UpdateGallery />}
              allowedRoles={["superadmin"]}
            />
          }
        />

         <Route
          path="/addblogs"
          element={
            <ProtectedRoute
              element={<AddBlogs />}
              allowedRoles={["superadmin"]}
            />
          }
        />

          <Route
          path="/updateblogs/:id"
          element={
            <ProtectedRoute
              element={<UpdateBlog />}
              allowedRoles={["superadmin"]}
            />
          }
        />

      </Routes>
    </div>
  );
}
